/**
 * Desktop employee MultiSelect scope for TrainingRecordAdd (trng012)
 * and TrainingInvitationAdd (trng028).
 *
 * Authoritative UX contract (same for both forms):
 * employee is eligible only if a TrainingCard exists with
 *   card.training_id == selected training_id
 *   if company selected: card.company_id == selected company_id
 *   if machines selected: selected ⊆ card.machine_ids (card may have extras)
 *   if lecturer type selected: card lecturer type == selected lecturer_id
 *     (card.lecturer_id ?? card.company_contact.lecturer_id ?? card.lecturer.id)
 *   employee is not departed (date_of_departure null/empty)
 *
 * company_contact_id / concrete trainer MUST NOT filter employees.
 * Contact is validated separately via assignment cascade (company + machines + lecturer type).
 *
 * BACKEND_ENFORCEMENT_GAP=true
 * - Record POST /api/training_records (TrainingRecordCreateContract) guarantees a
 *   TrainingCard for training_id, but does not enforce the full card assignment match.
 * - Invitation POST /api/training_invitations (assertEmployeesExist) guarantees the
 *   employee exists, not the full card assignment match.
 * Frontend filter is an intentional stricter business UX contract, not a backend invariant.
 */

import {
  normalizeMachineIds,
  normalizePositiveId,
  trainingIdsEqual,
} from './trainingSelectionCascade';

export function nestedTrainingCards(employeeRow) {
  return Array.isArray(employeeRow?.training_card) ? employeeRow.training_card : [];
}

export function employeeIdFromCardRow(employeeRow) {
  return normalizePositiveId(employeeRow?.id ?? employeeRow?.employee_id);
}

function cardsForTraining(employeeRow, trainingId) {
  const tid = normalizePositiveId(trainingId);
  if (tid == null) return [];
  return nestedTrainingCards(employeeRow).filter((row) => trainingIdsEqual(row?.training_id, tid));
}

export function employeeHasCardForTraining(employeeRow, trainingId) {
  return cardsForTraining(employeeRow, trainingId).length > 0;
}

export function isActiveTrainingEmployee(employeeRow) {
  const departed = employeeRow?.date_of_departure;
  return departed == null || String(departed).trim() === '';
}

/** Active training card: deactivation_date null/empty (create flows only). */
export function isActiveTrainingCard(cardRow) {
  const deactivated = cardRow?.deactivation_date;
  return deactivated == null || String(deactivated).trim() === '';
}

function cardCoversMachines(cardRow, machineIds) {
  const selected = normalizeMachineIds(machineIds);
  if (!selected.length) return true;
  const assigned = new Set(normalizeMachineIds(cardRow?.machines));
  return selected.every((id) => assigned.has(id));
}

export function cardLecturerTypeId(cardRow) {
  return cardRow?.lecturer_id
    ?? cardRow?.company_contact?.lecturer_id
    ?? cardRow?.lecturer?.id;
}

export function cardMatchesEmployeeEligibility(cardRow, assignment = {}) {
  if (!cardRow || typeof cardRow !== 'object') return false;

  const companyId = normalizePositiveId(assignment.companyId ?? assignment.company_id);
  if (companyId != null && !trainingIdsEqual(cardRow.company_id, companyId)) return false;

  const selectedMachines = assignment.machineIds ?? assignment.machines;
  if (!cardCoversMachines(cardRow, selectedMachines)) return false;

  const lecturerId = normalizePositiveId(assignment.lecturerTypeId ?? assignment.lecturer_id);
  if (lecturerId != null && !trainingIdsEqual(cardLecturerTypeId(cardRow), lecturerId)) return false;

  return true;
}

function toEmployeeOption(employeeRow) {
  const id = employeeIdFromCardRow(employeeRow);
  if (id == null) return null;
  return { id, name: employeeRow?.name ?? '' };
}

export function eligibleTrainingEmployees(trainingCards, trainingId, assignment = {}) {
  const tid = normalizePositiveId(trainingId);
  if (tid == null) return [];
  const seen = new Set();
  const out = [];
  for (const row of trainingCards || []) {
    if (!isActiveTrainingEmployee(row)) continue;
    const matching = cardsForTraining(row, tid).filter(isActiveTrainingCard);
    if (!matching.some((card) => cardMatchesEmployeeEligibility(card, assignment))) continue;
    const option = toEmployeeOption(row);
    if (!option || seen.has(option.id)) continue;
    seen.add(option.id);
    out.push(option);
  }
  return out;
}

export function keepEligibleTrainingEmployees(selected, trainingCards, trainingId, assignment = {}) {
  const allowed = new Set(
    eligibleTrainingEmployees(trainingCards, trainingId, assignment).map((item) => item.id)
  );
  return (selected || []).filter((item) => allowed.has(normalizePositiveId(item?.id ?? item)));
}

export function recordEmployeeOptions(trainingCards, trainingId, assignment = {}) {
  return eligibleTrainingEmployees(trainingCards, trainingId, assignment);
}

export function keepRecordEmployeesForTraining(selected, trainingCards, trainingId, assignment = {}) {
  return keepEligibleTrainingEmployees(selected, trainingCards, trainingId, assignment);
}

export function invitationEmployeeOptions(trainingCards, trainingId, assignment = {}) {
  return eligibleTrainingEmployees(trainingCards, trainingId, assignment);
}

export function keepInvitationEmployees(selected, trainingCards, trainingId, assignment = {}) {
  return keepEligibleTrainingEmployees(selected, trainingCards, trainingId, assignment);
}
